<template>
  <article
    :class="[
      'hobi-care-calendar-widget',
      !title
        ? 'hobi-care-calendar-widget--skeleton tw-p-4 tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-300 tw-box-border'
        : '',
    ]"
    @click="handleClick"
  >
    <div
      class="hobi-care-calendar-widget__wrapper tw-w-full tw-h-full tw-border tw-border-gray-300 tw-border-solid tw-rounded tw-py-12 tw-flex tw-relative tw-box-border tw-bg-white tw-cursor-pointer"
      v-if="title && !isLoading"
    >
      <div
        :class="[
          'hobi-care-calendar-widget__inner tw-flex tw-flex-col tw-px-4 tw-py-2',
          'hobi-care-calendar-widget__inner--' + careCalendarWidgetState,
          careCalendarWidgetState === widgetState.empty
            ? 'tw-w-full tw-justify-center tw-items-center tw-text-center'
            : '',
        ]"
      >
        <div
          class="hobi-care-calendar-widget__headline-wrapper tw-flex tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-p-4 tw-pb-0"
          v-if="title"
        >
          <VIcon
            class="tw-mr-2"
            symbol="care-calendar"
            size="44"
            viewBox="0 0 44 44"
          />
          <VHeadline class="tw-mt-0.5" :title="title" size="xs" />
        </div>
        <div
          class="hobi-care-calendar-widget__description-wrapper tw-mb-2"
          v-if="description"
        >
          <VHTMLText size="xs" :html-text="description" :router="router" />
        </div>
        <div
          class="hobi-care-calendar-widget__content-wrapper tw-flex tw-items-center"
        >
          <template
            v-if="careCalendarWidgetState == widgetState.quickEntry && images"
          >
            <div
              class="hobi-care-calendar-widget__content hobi-care-calendar-widget__content--quick-entry tw-flex tw-mr-4 tw-flex-shrink-0"
            >
              <div
                class="hobi-care-calendar-widget__todo-image tw-flex tw-justify-center tw-items-center tw-box-border tw-border tw-border-gray-300 tw-rounded tw-border-solid tw-flex-shrink-0 tw-w-12 tw-h-12"
                v-for="image in images.slice(0, 3)"
                :key="image"
              >
                <VImage :image="image" />
              </div>
            </div>
            <VHTMLText
              size="xs"
              :html-text="imageText"
              data-line-clamp="2"
              :router="router"
            />
          </template>
          <template v-if="careCalendarWidgetState == widgetState.toDo && todos">
            <div
              :class="[
                'hobi-care-calendar-widget__content tw-flex',
                'hobi-care-calendar-widget__content--todo tw-grid',
                todos.length === 1
                  ? 'hobi-care-calendar-widget__content--todo-single'
                  : 'tw-w-full tw-grid-cols-2',
              ]"
            >
              <div
                class="hobi-care-calendar-widget__todo-entry tw-flex tw-items-center"
                v-for="todo in todos.slice(0, 2)"
                :key="todo.title"
              >
                <div
                  class="hobi-care-calendar-widget__todo-image tw-flex tw-justify-center tw-items-center tw-box-border tw-border tw-border-gray-300 tw-rounded tw-border-solid tw-flex-shrink-0 tw-w-12 tw-h-12"
                >
                  <VImage :image="todo.image" />
                </div>
                <div
                  class="hobi-care-calendar-widget__todo-title"
                  data-line-clamp="3"
                >
                  <VHTMLText
                    size="xs"
                    :html-text="todo.title"
                    :router="router"
                  />
                </div>
              </div>
            </div>
            <VHTMLText
              size="xs"
              :html-text="imageText"
              v-if="imageText"
              :router="router"
            />
          </template>
        </div>
        <div
          :class="[
            'hobi-care-calendar-widget__footer-wrapper tw-flex tw-justify-evenly tw-items-center tw-min-h-12 tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-box-border',
            'hobi-care-calendar-widget__footer-wrapper--' +
              careCalendarWidgetState,
            careCalendarWidgetState != widgetState.quickEntry
              ? 'tw-border-0 tw-border-t tw-border-gray-300 tw-border-solid'
              : 'tw-grid tw-grid-cols-2 tw-gap-4 tw-p-4 tw-pt-0',
          ]"
        >
          <template v-if="careCalendarWidgetState == widgetState.quickEntry">
            <VHTMLText
              class="tw-text-green"
              size="xxs"
              :html-text="linkTeaser"
              v-if="linkTeaser"
              :router="router"
            />
            <VStyledLink
              :styled-link="link"
              button-size="widget-small"
              v-if="link"
              :router="router"
            />
          </template>
          <template v-else>
            <VLink
              type="button"
              :href="target"
              v-if="target && bottomText"
              :router="router"
              >{{ bottomText }}</VLink
            >
          </template>
        </div>
      </div>
    </div>
    <VCareCalendarWidgetSkeleton
      class="hobi-care-calendar-widget__skeleton"
      v-else
    />
  </article>
</template>

<script lang="ts">
import MetricsApiConnector from "@obi-dbs/metrics-api-connector";
import {
  VHeadline,
  VHTMLText,
  VIcon,
  VImage,
  VLink,
  VStyledLink,
} from "@openreply/pattern-library";
import { defineComponent, PropType } from "vue";

import { CareCalendarTodo, StyledLink, TrackingData } from "~/bff-types/types";
import VCareCalendarWidgetSkeleton from "~/components/VCareCalendarWidget/VCareCalendarWidgetSkeleton.vue";
import { router } from "~/services/router";
import {
  getParameterFromTrackingData,
  PromotionData,
  trackPromotionInteraction,
} from "~/tracking";
import { Logger } from "~/utils/logger";

enum ccWidgetState {
  quickEntry = "quick-entry",
  toDo = "todo",
  empty = "empty",
}

export default defineComponent({
  components: {
    VHeadline,
    VHTMLText,
    VStyledLink,
    VIcon,
    VImage,
    VLink,
    VCareCalendarWidgetSkeleton,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    images: {
      type: Array as PropType<string[]>,
    },
    todos: {
      type: Array as PropType<CareCalendarTodo[]>,
    },
    imageText: {
      type: String,
      default: "",
    },
    linkTeaser: {
      type: String,
      default: "",
    },
    link: {
      type: Object as PropType<StyledLink>,
      default: () => ({}),
    },
    target: {
      type: String,
      default: "",
    },
    bottomText: {
      type: String,
      default: "",
    },
    trackingData: {
      type: Object as PropType<TrackingData | null>,
      default: undefined as undefined | TrackingData | null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    };

    const observer = new IntersectionObserver(this.handleIntersection, options);
    const target = this.$el;
    if (target) observer.observe(target);
  },
  methods: {
    handleIntersection(entries) {
      if (entries.some((e) => e.isIntersecting)) this.trackViewPromotion();
    },
    trackViewPromotion() {
      MetricsApiConnector.trackEvent("viewPromotion", {
        promotion: {
          promotionId: "",
          promotionName: getParameterFromTrackingData(this.trackingData, "521"),
          promotionPlacement: "care_calender_widget",
        },
      }).catch((e) => {
        Logger.logError(
          "[Tracking VCareCalendarWidget] Error with MetricsApi",
          e
        );
      });
    },
    handleClick() {
      router.handleLink(this.overallWidgetLink);
      const interactionId = "care_calender_widget.clicked";
      const interactionValue = "";
      const promotionData: PromotionData = {
        promotionId: "",
        promotionName: getParameterFromTrackingData(this.trackingData, "521"),
        promotionPlacement: "care_calender_widget",
      };

      trackPromotionInteraction(interactionId, interactionValue, promotionData);
    },
  },
  computed: {
    careCalendarWidgetState() {
      if (this.images && !this.todos) {
        return ccWidgetState.quickEntry;
      }
      if (!this.images && this.todos) {
        return ccWidgetState.toDo;
      }
      if (!this.images && !this.todos) {
        return ccWidgetState.empty;
      }

      return ccWidgetState.empty;
    },
    overallWidgetLink() {
      if (
        this.careCalendarWidgetState === ccWidgetState.quickEntry &&
        this.link
      ) {
        return this.link.url;
      }
      if (
        this.careCalendarWidgetState !== ccWidgetState.quickEntry &&
        this.target
      ) {
        return this.target;
      }
      return "";
    },
  },
  setup() {
    const widgetState = ccWidgetState;

    return { widgetState, router };
  },
});
</script>

<style scoped>
.hobi-care-calendar-widget__wrapper,
.hobi-care-calendar-widget--skeleton {
  max-width: var(--hobi-widget-width);
  min-height: var(--hobi-widget-height);
}

.hobi-care-calendar-widget .hobi-link {
  -webkit-tap-highlight-color: transparent;
}

.hobi-care-calendar-widget__todo-image:not(:last-child) {
  margin-right: var(--hobi-space-s);
}

.hobi-care-calendar-widget__todo-title {
  hyphens: auto;
}

.hobi-care-calendar-widget__footer-wrapper {
  min-height: var(--hobi-space-3xl);
}

.hobi-care-calendar-widget__footer-wrapper.hobi-care-calendar-widget__footer-wrapper--quick-entry
  .hobi-text {
  color: var(--hobi-color-green);
}
</style>
